'use client';

import { useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/nextjs';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';

const roleOptions = [
  { value: 'student', label: 'Student' },
  { value: 'professional', label: 'Professional' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'founder', label: 'Founder' },
  { value: 'researcher', label: 'Researcher' },
  { value: 'creator', label: 'Creator' },
  { value: 'other', label: 'Other' },
] as const;

const discoveryOptions = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'x-twitter', label: 'X / Twitter' },
  { value: 'friend-or-colleague', label: 'Friend or colleague' },
  { value: 'search', label: 'Search engine' },
  { value: 'school-or-work', label: 'School or work' },
  { value: 'product-hunt', label: 'Product Hunt' },
  { value: 'other', label: 'Other' },
] as const;

const useOptions = [
  { value: 'study-and-revision', label: 'Study and revision' },
  { value: 'teaching-and-explaining', label: 'Teaching and explaining' },
  { value: 'research-and-reading', label: 'Research and reading' },
  { value: 'presentations-and-work', label: 'Presentations and work' },
  { value: 'content-and-storytelling', label: 'Content and storytelling' },
  { value: 'general-learning', label: 'General learning' },
] as const;

interface OnboardingStatusResponse {
  completed: boolean;
  onboardingCompletedAt?: string | null;
  preferredName?: string | null;
  role?: string | null;
  discoverySource?: string | null;
  intendedUse?: string | null;
  notes?: string | null;
}

export function OnboardingModal() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preferredName, setPreferredName] = useState('');
  const [role, setRole] = useState('student');
  const [discoverySource, setDiscoverySource] = useState('linkedin');
  const [intendedUse, setIntendedUse] = useState('study-and-revision');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isSignedIn) {
      setOpen(false);
      setLoadingStatus(false);
      return;
    }

    let cancelled = false;

    async function loadStatus() {
      try {
        setLoadingStatus(true);
        const response = await fetch('/api/users/me/onboarding');
        if (!response.ok) {
          throw new Error('Failed to load onboarding status');
        }

        const data = (await response.json()) as OnboardingStatusResponse;
        if (cancelled) {
          return;
        }

        setPreferredName(data.preferredName || user?.firstName || user?.fullName || '');
        setRole(data.role || 'student');
        setDiscoverySource(data.discoverySource || 'linkedin');
        setIntendedUse(data.intendedUse || 'study-and-revision');
        setNotes(data.notes || '');
        setOpen(!data.completed);
      } catch (loadError) {
        if (!cancelled) {
          console.error('[OnboardingModal] Failed to load onboarding status:', loadError);
        }
      } finally {
        if (!cancelled) {
          setLoadingStatus(false);
        }
      }
    }

    void loadStatus();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, user?.firstName, user?.fullName]);

  async function handleSubmit() {
    const trimmedName = preferredName.trim();
    if (!trimmedName) {
      setError('Please tell us what to call you.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/users/me/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferredName: trimmedName,
          role,
          discoverySource,
          intendedUse,
          notes,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save onboarding');
      }

      setOpen(false);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to save onboarding');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isSignedIn || loadingStatus) {
    return null;
  }

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] overflow-y-auto border-border/60 bg-background shadow-2xl sm:max-w-xl"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogTitle className="sr-only">Set up your profile</DialogTitle>

        <div className="space-y-6 px-6 py-6">
          <FieldGroup>
            <Field>
              <FieldLabel>What should we call you?</FieldLabel>
              <Input
                value={preferredName}
                onChange={(event) => setPreferredName(event.target.value)}
                placeholder="Your name"
              />
            </Field>

            <Field>
              <FieldLabel>Which best describes you?</FieldLabel>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel>How did you hear about us?</FieldLabel>
              <Select value={discoverySource} onValueChange={setDiscoverySource}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {discoveryOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel>How do you want to use Galexia?</FieldLabel>
              <Select value={intendedUse} onValueChange={setIntendedUse}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {useOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel>Anything else we should know?</FieldLabel>
              <Textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Optional"
                rows={4}
              />
            </Field>
          </FieldGroup>

          {error && (
            <div
              className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end">
            <Button size="lg" onClick={handleSubmit} disabled={submitting} className="min-w-40">
              {submitting ? 'Saving...' : 'Continue'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
